import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { subMinutes } from 'date-fns'
import axios from 'axios'
import {
  Alert,
  Breadcrumbs,
  Button,
  Chip,
  Container,
  Grid,
  Link,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { Add, GppMaybe } from '@mui/icons-material'
import { AssetGroupType } from '../enums'
import { usePolicy } from '../hooks'
import { useAssetGroupLoader } from '../loaders'
import { Aurora, AuroraAssetGroup, AuroraAwhpEvent, AuroraAwhpData, Sensor } from '../types'
import { getSensorNow } from '../services/deviceService'
import { getAuroraAwhpActiveEvents, getAuroraAwhpDataNow } from '../services/aurora'
import NotFound from './error/NotFound'
import LoadingState from '../components/LoadingState'
import PageHeader from '../components/PageHeader'
import AuroraAssetGroupControls from '../components/AuroraAssetGroupControls'
import AuroraAssetGroupAlarmStatusChip from '../components/AuroraAssetGroupAlarmStatusChip'
import AuroraAssetGroupDefrostStatusChip from '../components/AuroraAssetGroupDefrostStatusChip'
import AuroraAssetGroupSummary from '../components/AuroraAssetGroupSummary'
import AuroraAssetGroupStack from '../components/AuroraAssetGroupStack'

function isAuroraAssetGroup(value: unknown): value is AuroraAssetGroup {
  if (typeof value !== 'object' || value === null || 'type' in value === false) {
    return false
  }

  return value.type === AssetGroupType.AURORA
}

export default function AuroraAssetGroupPage() {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The policy hook.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The AWHP data indexed by aurora ID.
   */
  const [auroraAwhpData, setAuroraAwhpData] = useState<Record<string, AuroraAwhpData>>({})

  /**
   * The sensors by indexed device ID.
   */
  const [sensors, setSensors] = useState<Record<string, Sensor>>({})

  /**
   * The active events indexed by aurora ID.
   */
  const [auroraAwhpActiveEvents, setAuroraAwhpActiveEvents] = useState<
    Record<string, AuroraAwhpEvent[]>
  >({})
  const [auroraAwhpDataError, setAuroraAwhpDataError] = useState<string | null>(null)

  /**
   * The route params.
   */
  const { id } = useParams()

  /**
   * The asset group loader.
   */
  const { assetGroup, loading } = useAssetGroupLoader(id)

  /**
   * The auroras.
   */
  const auroras = useMemo(() => {
    if (assetGroup?.assets === undefined) {
      return []
    }

    return assetGroup.assets
      .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
      .map(({ aurora, ...asset }) => ({ ...aurora!, asset }) as Aurora)
  }, [assetGroup?.assets])

  const auroraIds = useMemo(() => auroras.map((aurora) => aurora.id), [auroras])

  const deviceIds = useMemo(
    () =>
      auroras
        .map((aurora) => aurora.deviceId)
        .filter((deviceId) => deviceId !== null && deviceId !== undefined),
    [auroras]
  )

  useEffect(() => {
    const controller = new AbortController()

    loadAuroraAwhpData(auroraIds, controller)
    loadAuroraAwhpActiveEvents(auroraIds, controller)
    loadSensors(deviceIds, controller)

    const ONE_MINUTE = 60 * 1000
    const interval = setInterval(() => {
      loadAuroraAwhpData(auroraIds, controller)
      loadAuroraAwhpActiveEvents(auroraIds, controller)
      loadSensors(deviceIds, controller)
    }, ONE_MINUTE)

    return () => {
      clearInterval(interval)
      controller.abort()
    }
  }, [auroraIds, deviceIds])

  async function loadAuroraAwhpData(auroraIds: string[], controller: AbortController) {
    const auroraAwhpData: Record<string, AuroraAwhpData> = {}
    let auroraAwhpDataError: string | null = null

    for (const auroraId of auroraIds) {
      try {
        const auroraAwhpRecord = await getAuroraAwhpDataNow(auroraId, { signal: controller.signal })
        auroraAwhpData[auroraId] = auroraAwhpRecord
      } catch (error) {
        if (axios.isCancel(error)) {
          return
        }

        auroraAwhpDataError =
          error instanceof Error ? error.message : 'Failed to load Aurora AWHP data'
      }
    }

    setAuroraAwhpData(auroraAwhpData)
    setAuroraAwhpDataError(auroraAwhpDataError)
  }

  async function loadAuroraAwhpActiveEvents(auroraIds: string[], controller: AbortController) {
    const activeEvents: Record<string, AuroraAwhpEvent[]> = {}

    for (const auroraId of auroraIds) {
      try {
        activeEvents[auroraId] = await getAuroraAwhpActiveEvents(auroraId, {
          signal: controller.signal
        })
      } catch {
        // We can ignore the error.
      }
    }

    setAuroraAwhpActiveEvents(activeEvents)
  }

  async function loadSensors(deviceIds: string[], controller: AbortController) {
    const sensors: Record<string, Sensor> = {}

    for (const deviceId of deviceIds) {
      try {
        const sensor = await getSensorNow(deviceId, { signal: controller.signal })
        sensors[deviceId] = sensor
      } catch {
        // Ignore the error.
      }
    }

    setSensors(sensors)
  }

  // Check that the loaded asset group is actually an aurora asset group.
  if (!loading && assetGroup && !isAuroraAssetGroup(assetGroup)) {
    return <NotFound />
  }

  return (
    <Container maxWidth={false}>
      <PageHeader
        title={assetGroup?.name || t('loading')}
        breadcrumbs={
          <Breadcrumbs>
            <Link component={NavLink} to="/">
              {t('breadcrumbs:frontpage')}
            </Link>

            {assetGroup?.project ? (
              <Link component={NavLink} to="/projects">
                {t('breadcrumbs:projects')}
              </Link>
            ) : isSuperadmin ? (
              <Link component={NavLink} to="/auroras">
                Arctic Warm
              </Link>
            ) : null}

            {!assetGroup?.project && isSuperadmin && (
              <Link component={NavLink} to="/auroras#stacks">
                {t('auroraStacks')}
              </Link>
            )}

            {assetGroup?.project && (
              <Link component={NavLink} to={`/projects/${assetGroup.project.id}`}>
                {assetGroup.project.name}
              </Link>
            )}

            {assetGroup?.project && (
              <Link component={NavLink} to={`/projects/${assetGroup.project.id}#monitoring`}>
                {t('devices')}
              </Link>
            )}

            {assetGroup ? (
              <Typography title={assetGroup.name}>{assetGroup.name}</Typography>
            ) : (
              <Skeleton variant="text" width={100} />
            )}
          </Breadcrumbs>
        }
        action={
          <>
            {assetGroup && assetGroup.projectId !== null && (
              <Button
                variant="contained"
                color="primary"
                component={NavLink}
                endIcon={<Add />}
                to={`/install/aurora?projectId=${assetGroup.projectId}&assetGroupId=${assetGroup.id}&skipSelectProject=true`}
              >
                {t('installNewAuroraBlock')}
              </Button>
            )}
          </>
        }
      />
      {auroraAwhpDataError && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          {auroraAwhpDataError}
        </Alert>
      )}
      {loading ? (
        <LoadingState />
      ) : (
        <Grid container spacing={6}>
          {assetGroup && isAuroraAssetGroup(assetGroup) && (
            <Grid item xs={12}>
              <AuroraAssetGroupSystemStatus
                auroraAssetGroup={assetGroup}
                auroraAwhpData={auroraAwhpData}
                auroraAwhpActiveEvents={auroraAwhpActiveEvents}
                sensors={sensors}
              />
            </Grid>
          )}

          {assetGroup && isAuroraAssetGroup(assetGroup) && (
            <Grid item xs={12}>
              <AuroraAssetGroupStack
                auroraAssetGroup={assetGroup}
                auroraAwhpData={auroraAwhpData}
                auroraAwhpActiveEvents={auroraAwhpActiveEvents}
                sensors={sensors}
              />
            </Grid>
          )}

          {assetGroup && isAuroraAssetGroup(assetGroup) && (
            <Grid item xs={12}>
              <AuroraAssetGroupControls
                auroraAssetGroup={assetGroup}
                auroraAwhpData={auroraAwhpData}
                sensors={sensors}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  )
}

interface AuroraAssetGroupSystemStatusProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData: Record<string, AuroraAwhpData>
  auroraAwhpActiveEvents: Record<string, AuroraAwhpEvent[]>
  sensors: Record<string, Sensor>
}

function AuroraAssetGroupSystemStatus({
  auroraAssetGroup,
  auroraAwhpData,
  auroraAwhpActiveEvents,
  sensors
}: AuroraAssetGroupSystemStatusProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('aurora')

  /**
   * The theme hook.
   */
  const theme = useTheme()

  /**
   * The auroras.
   */
  const auroras = useMemo(() => {
    if (auroraAssetGroup?.assets === undefined) {
      return []
    }

    return auroraAssetGroup.assets
      .filter((asset) => asset.aurora !== undefined && asset.aurora !== null)
      .map(({ aurora, ...asset }) => ({ ...aurora!, asset }) as Aurora)
  }, [auroraAssetGroup?.assets])

  const runningCount = useMemo(() => {
    let counter = 0
    for (const aurora of auroras) {
      if (aurora.id in auroraAwhpData) {
        const awhpReading = auroraAwhpData[aurora.id]
        if (new Date(awhpReading.time) > subMinutes(new Date(), 3)) {
          counter++
        }
      }
    }
    return counter
  }, [auroras, auroraAwhpData])

  const totalCount = auroras.length

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        background: theme.palette.background.paper,
        py: 4,
        px: 6,
        '& .MuiAlert-message': { width: '100%' }
      }}
    >
      <Stack justifyContent="space-between" gap={6} direction={{ xs: 'column', desktop: 'row' }}>
        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={2} alignItems="center">
            <GppMaybe fontSize="large" />
            <Typography variant="h6">{t('systemStatus')}</Typography>
          </Stack>

          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" sx={{ pl: 10 }}>
            <AuroraAssetGroupAlarmStatusChip
              auroraAssetGroup={auroraAssetGroup}
              auroraAwhpActiveEvents={auroraAwhpActiveEvents}
              size="small"
            />

            <AuroraAssetGroupDefrostStatusChip
              auroraAssetGroup={auroraAssetGroup}
              auroraAwhpActiveEvents={auroraAwhpActiveEvents}
              size="small"
            />

            <Chip
              size="small"
              variant="outlined"
              label={`${runningCount}/${totalCount} ${t('running').toLowerCase()}`}
              color="success"
            />
          </Stack>
        </Stack>

        <AuroraAssetGroupSummary
          auroraAssetGroup={auroraAssetGroup}
          auroraAwhpData={auroraAwhpData}
          sensors={sensors}
        />
      </Stack>
    </Alert>
  )
}
